import React, { useState, useEffect, useContext } from 'react';

export type FontSize = 'small' | 'normal' | 'large';
export type Theme = 'dark' | 'light';

interface DisplayPreferences {
    showCents: boolean;
    fontSize: FontSize;
    theme: Theme;
}

interface DisplayContextType {
    prefs: DisplayPreferences;
    toggleShowCents: (enabled: boolean) => void;
    setFontSize: (size: FontSize) => void;
    setTheme: (theme: Theme) => void;
}

const DisplayContext = React.createContext<DisplayContextType | undefined>(undefined);

export const useDisplay = (): DisplayContextType => {
    const context = useContext(DisplayContext);
    if (!context) {
        throw new Error('useDisplay must be used within DisplayProvider');
    }
    return context;
};

interface DisplayProviderProps {
    children: React.ReactNode;
}

export const DisplayProvider: React.FC<DisplayProviderProps> = ({ children }) => {
    const [prefs, setPrefs] = useState<DisplayPreferences>({
        showCents: true,
        fontSize: 'normal',
        theme: 'dark'
    });

    // Apply CSS variables when preferences change
    useEffect(() => {
        applyDisplayPreferences(prefs);
    }, [prefs]);

    const toggleShowCents = (enabled: boolean) => {
        setPrefs(prev => ({ ...prev, showCents: enabled }));
    };

    const setFontSize = (size: FontSize) => {
        setPrefs(prev => ({ ...prev, fontSize: size }));
    };

    const setTheme = (theme: Theme) => {
        setPrefs(prev => ({ ...prev, theme }));
    };

    return (
        <DisplayContext.Provider value={{ prefs, toggleShowCents, setFontSize, setTheme }}>
            {children}
        </DisplayContext.Provider>
    );
};

/**
 * Apply display preferences to the DOM via CSS variables
 */
function applyDisplayPreferences(prefs: DisplayPreferences) {
    const root = document.documentElement;

    // Font size mapping
    const fontSizeMap = {
        small: '12px',
        normal: '14px',
        large: '16px'
    };

    root.style.setProperty('--base-font-size', fontSizeMap[prefs.fontSize]);
    root.style.setProperty('--theme', prefs.theme);

    // Apply theme class to body for CSS selectors
    document.body.classList.remove('theme-dark', 'theme-light');
    document.body.classList.add(`theme-${prefs.theme}`);
}
